import { useState } from 'react'
import {Send, CheckCircle2, AlertCircle, Calendar} from 'lucide-react'

const problemTypes = [
  'Fuite d\'eau',
  'WC bouché',
  'Canalisation bouchée',
  'Chauffe-eau en panne',
  'Chauffage / Chaudière en panne',
  'Installation sanitaire',
  'Entretien / maintenance',
  'Autre',
]

export default function ContactForm({ compact = false }: { compact?: boolean }) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [problem, setProblem] = useState(problemTypes[0])
  const [urgent, setUrgent] = useState(true)
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!phone.trim() || !name.trim()) {
      setStatus('error')
      return
    }
    setStatus('sending')
    // Simulation d'envoi (à brancher sur backend Lumi/Deno function ultérieurement)
    setTimeout(() => {
      setStatus('success')
      setName('')
      setPhone('')
      setMessage('')
    }, 900)
  }

  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-6 lg:p-8 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-green-500 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <h3 className="text-lg lg:text-xl font-bold text-navy-700 mb-2">Demande reçue !</h3>
        <p className="text-navy-600 text-sm">
          Nous vous rappelons dans les plus brefs délais. Pour une urgence, appelez directement le <a href="tel:+41796430399" className="text-urgent-600 font-bold">079 643 03 99</a>.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-5 text-sm font-semibold text-navy-600 hover:text-navy-700 underline"
        >
          Envoyer une autre demande
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={`bg-white rounded-2xl ${compact ? 'p-5' : 'p-6 lg:p-8'} shadow-elevated border border-navy-100`}>
      {!compact && (
        <div className="mb-6">
          <h3 className="text-xl lg:text-2xl font-extrabold text-navy-700">Demander une intervention</h3>
          <p className="text-sm text-navy-500 mt-1">Réponse rapide. Devis clair et sans surprise.</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-navy-700 uppercase tracking-wider mb-1.5">Nom complet *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-urgent-500 focus:ring-2 focus:ring-urgent-100 outline-none transition-all text-navy-700 placeholder:text-navy-300"
            placeholder="Votre nom"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-navy-700 uppercase tracking-wider mb-1.5">Téléphone *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-urgent-500 focus:ring-2 focus:ring-urgent-100 outline-none transition-all text-navy-700 placeholder:text-navy-300"
            placeholder="+41 79 000 00 00"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-navy-700 uppercase tracking-wider mb-1.5">Type de problème</label>
          <select
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-urgent-500 focus:ring-2 focus:ring-urgent-100 outline-none transition-all text-navy-700 bg-white"
          >
            {problemTypes.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 p-3.5 rounded-xl bg-urgent-50 border border-urgent-100">
          <input
            id="urgent"
            type="checkbox"
            checked={urgent}
            onChange={(e) => setUrgent(e.target.checked)}
            className="w-5 h-5 rounded accent-urgent-500"
          />
          <label htmlFor="urgent" className="text-sm font-semibold text-navy-700 cursor-pointer select-none">
            Intervention urgente (sous 1 heure)
          </label>
        </div>

        {!compact && (
          <div>
            <label className="block text-xs font-bold text-navy-700 uppercase tracking-wider mb-1.5">Message (optionnel)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-urgent-500 focus:ring-2 focus:ring-urgent-100 outline-none transition-all text-navy-700 placeholder:text-navy-300 resize-none"
              placeholder="Décrivez brièvement le problème..."
            />
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-2 text-sm text-urgent-700 bg-urgent-50 border border-urgent-200 rounded-lg px-3 py-2.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Veuillez renseigner votre nom et votre numéro de téléphone.
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'sending'}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow hover:scale-[1.01] active:scale-[0.99] transition-transform disabled:opacity-70"
        >
          {status === 'sending' ? 'Envoi...' : (
            <>
              <Send className="w-4 h-4" strokeWidth={2.5} />
              Envoyer ma demande
            </>
          )}
        </button>

        <p className="text-[11px] text-navy-400 text-center">
          En envoyant ce formulaire, vous acceptez notre politique de confidentialité.
        </p>

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-navy-100" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-[11px] font-bold uppercase tracking-wider text-navy-400">ou</span>
          </div>
        </div>

        <a
          href="https://fixnow.ch"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-navy-700 hover:bg-navy-600 text-white font-bold transition-colors"
        >
          <Calendar className="w-4 h-4" strokeWidth={2.5} />
          Voir notre disponibilité sur FixNow
        </a>
        <p className="text-[11px] text-navy-400 text-center">
          Réservation rapide via FixNow.ch
        </p>
      </div>
    </form>
  )
}
